import React, { useEffect, useState } from 'react';
import {
  ChatData,
  ChatListItem,
  ChatUnreadCount,
  UserFilters,
} from '../types/types';
import { useDebounce } from 'hooks/useDebounce';
import { groupListService } from '../services/chatServices';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ChatListItemsSkeleton } from './ChatSkeletons';
import { getLastAction, handelActiveChat } from '../utils/renderSentMessage';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import CreateGroupe from './CreateGroupe';
import { useSignalR } from 'contexts/SignalRProvider';

interface GroupeListProps {
  onSelectUser: (user: ChatListItem) => Promise<void>;
  isGroup: boolean;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  externalSearch?: string;
  onCount?: (count: number) => void;
  activeId?: string | null;
}
const GroupeList: React.FC<GroupeListProps> = ({
  onSelectUser,
  isGroup,
  refresh,
  setRefresh,
  externalSearch,
  onCount,
  activeId,
}) => {
  const { t } = useTranslation();
  const [groupes, setGroupes] = useState<ChatData | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const useExternal = externalSearch !== undefined;
  const [filters, setFilters] = useState<UserFilters>({
    searchValue: '',
    lenght: 5,
    currentList: 0,
    id: 0,
    roleName: '',
    userid: '',
    roleId: '',
    ids: [],
    selectAll: false,
    isGroup: true,
  });
  const debouncedFilters = useDebounce(filters, 300);
  const { openSidebar } = useSidebarManager();
  const { connection } = useSignalR();

  useEffect(() => {
    if (!connection) return;
    const handler = (data: ChatUnreadCount) => {
      setGroupes((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          chatList: prev.chatList.map((item) => {
            const conv = data.byConversation.find(
              (c) => c.id === item.chatUser.value && c.isGroup === item.isGroup,
            );
            return { ...item, messageUnread: conv?.unread ?? 0 };
          }),
        };
      });
    };
    connection.on('ChatUnreadCountUpdated', handler);
    return () => {
      connection.off('ChatUnreadCountUpdated', handler);
    };
  }, [connection]);

  useEffect(() => {
    const fetchUsers = async (): Promise<void> => {
      const response = await groupListService(debouncedFilters);
      if (!response.success) {
        console.error(response.message, 'Failed to fetch group list');
        return;
      }
      const res = response.data;
      setGroupes((prev) => {
        if (prev === null || filters.searchValue || filters.currentList === 0) {
          return {
            ...res,
            chatList: [...res.chatList],
          };
        }

        return {
          ...prev,
          chatList: prev.chatList.concat(res.chatList),
        };
      });
    };
    fetchUsers();
  }, [debouncedFilters]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      currentList: 0,
    }));
    setGroupes(null);
  }, [refresh]);

  useEffect(() => {
    if (!useExternal) return;
    setFilters((prev) => ({
      ...prev,
      searchValue: externalSearch ?? '',
      currentList: 0,
    }));
  }, [externalSearch, useExternal]);

  useEffect(() => {
    if (onCount && groupes) onCount(groupes.chatList.length);
  }, [groupes, onCount]);

  const handleSearchChange = (value: string): void => {
    setSearchValue(value);
    setFilters((prev) => ({
      ...prev,
      searchValue: value,
      currentList: 0,
    }));
  };

  const handleSelectGroup = async (item: ChatListItem): Promise<void> => {
    await onSelectUser(item);
  };

  const handleKeySelectGroup = async (
    event: React.KeyboardEvent<HTMLDivElement>,
    item: ChatListItem,
  ): Promise<void> => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handelActiveChat(
        event as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
      );
      await onSelectUser(item);
    }
  };

  return (
    <div
      className={`tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-relative ${isGroup ? '' : 'tw-hidden'}`}
    >
      {/* Search (hidden when an external search is provided by the parent) */}
      {!useExternal ? (
        <div className="tw-px-4 tw-pt-3 tw-pb-3 tw-border-b tw-border-gray3">
          <div className="tw-relative">
            <span className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none tw-flex tw-items-center">
              <img
                className="tw-w-4 tw-h-4 tw-icon-gray tw-opacity-70"
                src="/icons/search.svg"
                alt=""
              />
            </span>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('chat.search.groups.placeholder', {
                defaultValue: 'Search groups',
              })}
              className="tw-w-full tw-h-10 tw-rounded-full tw-bg-white tw-border tw-border-gray3 tw-pl-11 tw-pr-10 tw-text-sm tw-text-dark tw-outline-none focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary-opacity tw-transition tw-placeholder:tw-text-dark-gray4"
            />
            {searchValue ? (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-transparent hover:tw-bg-light-gray2 tw-border-none tw-cursor-pointer tw-transition"
                aria-label="Clear search"
              >
                <img
                  className="tw-w-3 tw-h-3 tw-icon-gray"
                  src="/icons/close.svg"
                  alt=""
                />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* List */}
      <div
        className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-custom-scrollbar"
        id="users-list"
      >
        {groupes && groupes.chatList.length > 0 ? (
          <div className="tw-divide-y tw-divide-light-gray3">
            {groupes?.chatList.map((item, index) => {
              const isUnread = item.messageUnread > 0;
              const previewText = getLastAction(item);
              const hasPreview = previewText.length > 0;
              const hasFile = !!item.lastAction?.fileName;

              return (
                <div
                  key={index}
                  data-id={item.chatUser?.value}
                  className={`tw-relative tw-grid tw-items-center tw-gap-3 tw-px-[calc(var(--unit-minthr)*22)] tw-py-2 tw-cursor-pointer tw-transition ${
                    isUnread
                      ? 'tw-bg-primary-opacity/40'
                      : 'tw-bg-transparent hover:tw-bg-primary-light4'
                  } ${item.groupsEnd ? 'groups-end' : ''} ${item.isGroup ? 'group' : ''}`}
                  style={{
                    gridTemplateColumns: 'calc(var(--unit-minthr)*40) 1fr auto',
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    handelActiveChat(e);
                    void handleSelectGroup(item);
                  }}
                  onKeyDown={(e) => {
                    void handleKeySelectGroup(e, item);
                  }}
                >
                  <div className="minthr-flex minthr-flex-items-center minthr-flex-justify-center">
                    {item.chatUser.imagePath != null ? (
                      <img
                        className="tw-w-9 tw-h-9 tw-rounded-full tw-object-cover"
                        src={`/upload/profileimages/${item.chatUser?.value}/${item.chatUser?.imagePath}`}
                        alt=""
                      />
                    ) : (
                      <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-primary-opacity tw-flex tw-items-center tw-justify-center">
                        <img
                          className="tw-w-[calc(var(--unit-minthr)*18)] tw-h-[calc(var(--unit-minthr)*18)] tw-icon-primary"
                          src="/icons/users.svg"
                          alt=""
                        />
                      </div>
                    )}
                  </div>

                  <div className="tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span
                        className={`tw-truncate tw-text-[calc(var(--unit-minthr)*12)] tw-leading-tight name ${
                          isUnread
                            ? 'tw-font-bold tw-text-dark'
                            : 'tw-font-semibold tw-text-dark'
                        }`}
                      >
                        # {item.chatUser.text}
                      </span>
                    </div>
                    <div className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 lastmessage">
                      {hasPreview && hasFile && (
                        <svg
                          className="tw-shrink-0 tw-text-dark-gray4"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                          <path d="M14 3v5h5" />
                        </svg>
                      )}
                      <span
                        className={`tw-truncate tw-text-[calc(var(--unit-minthr)*12)] tw-flex-1 ${
                          !hasPreview
                            ? 'tw-italic tw-text-dark-gray4'
                            : isUnread
                              ? 'tw-font-medium tw-text-dark-gray2'
                              : 'tw-text-dark-gray3'
                        }`}
                      >
                        {hasPreview
                          ? previewText
                          : t('chat.start.conversation', {
                              defaultValue: 'Start a conversation',
                            })}
                      </span>
                    </div>
                  </div>

                  <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-min-w-[calc(var(--unit-minthr)*40)]">
                    <span
                      className={`tw-text-[calc(var(--unit-minthr)*11)] tw-whitespace-nowrap tw-tabular-nums tw-leading-none date ${
                        isUnread
                          ? 'tw-font-bold tw-text-primary'
                          : 'tw-font-medium tw-text-dark-gray4'
                      }`}
                    >
                      {item.lastAction?.date
                        ? moment(item.lastAction.date).format('HH:mm')
                        : '—'}
                    </span>
                    {isUnread && (
                      <span
                        className="tw-inline-grid tw-place-items-center tw-min-w-[calc(var(--unit-minthr)*18)] tw-h-[calc(var(--unit-minthr)*18)] tw-px-1 tw-rounded-full tw-bg-primary tw-text-[calc(var(--unit-minthr)*10)] tw-font-bold tw-text-white"
                        data-value={item.messageUnread}
                      >
                        {item.messageUnread > 99 ? '99+' : item.messageUnread}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
            {groupes?.chatList.length === 0 ? (
              <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-6 tw-px-6 tw-py-12">
                {/* Illustration */}
                <div className="tw-relative tw-w-12 tw-h-12">
                  <div className="tw-absolute tw-inset-0 tw-w-12 tw-h-12 tw-rounded-full tw-bg-primary-opacity tw-animate-pulse tw-opacity-40" />
                  <div className="tw-relative tw-w-full tw-h-full tw-rounded-full tw-bg-primary-opacity tw-flex tw-items-center tw-justify-center tw-shadow-sm">
                    <img
                      className="tw-w-6 tw-h-6 tw-icon-primary "
                      src="/icons/users.svg"
                      alt=""
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
                  <h3 className="tw-text-[calc(var(--unit-minthr)*13)] tw-font-semibold tw-text-dark tw-m-0">
                    {t('chat.no.group', { defaultValue: 'No groups yet' })}
                  </h3>
                  <p className="tw-text-[calc(var(--unit-minthr)*11)] tw-text-dark-gray3 tw-m-0 tw-leading-relaxed">
                    {t('chat.no.group.description', {
                      defaultValue:
                        'Create a group to collaborate with your team members',
                    })}
                  </p>
                </div>

                {/* Action Button */}
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-primary hover:tw-bg-primary-dark tw-text-white tw-font-semibold tw-border-none tw-cursor-pointer tw-shadow-sm tw-transition"
                  onClick={() => {
                    openSidebar(CreateGroupe, false, {
                      setRefresh,
                    });
                  }}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  {t('chat.new.group', { defaultValue: 'New group' })}
                </button>
              </div>
            ) : (
              <ChatListItemsSkeleton />
            )}
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      {isGroup && groupes && groupes.chatList.length > 0 && (
        <button
          type="button"
          onClick={() => openSidebar(CreateGroupe, false, { setRefresh })}
          className="tw-absolute tw-bottom-6 tw-right-6 tw-w-8 tw-h-8 tw-rounded-md tw-bg-primary hover:tw-bg-primary-dark tw-text-white tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 hover:tw-scale-110 tw-z-20 tw-border-none tw-cursor-pointer"
          title={t('chat.new.group', { defaultValue: 'New Group' })}
        >
          <svg
            width="calc(var(--unit-minthr)*15)"
            height="calc(var(--unit-minthr)*15)"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default GroupeList;
